import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppBar } from "../../../components/mobileUi";
import {
  CenterSpinner,
  EmptyText,
  ExpandableCard,
  LoadMore,
  M,
  RowKV,
  SummaryCard,
} from "../../../components/managementUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../../components/desktopUi";
import { formatMoneyUS } from "../../../utils/financeFormat";
import {
  getIncomeSummaryAreaLockingsApi,
  getIncomeSummaryBlockLockingsApi,
  getIncomeSummaryEmployeeLockingsApi,
} from "../../../api/incomeManager.api";
import { EMP_BASE_PATH } from "../../../config";

const LIMIT = 10;
const vnd = (v) => `${formatMoneyUS(v || 0)} VNĐ`;

// Khớp app: management/income/{employee|area|block}/locking-list.tsx
const KINDS = {
  "nhan-vien": {
    title: (name) => "Thu nhập nhân viên",
    listApi: getIncomeSummaryEmployeeLockingsApi,
    idParam: "employeeId",
    expandable: false,
    cardTitle: (item) =>
      `${item?.employeeObject?.fullName || ""} ${item?.employeeObject?.fullName ? "-" : ""} ${
        item?.employeeObject?.code || ""
      }`.trim(),
    rows: (item) => [
      { label: "Sản phẩm", value: item?.apartment?.code },
      { label: "Dự án", value: item?.project?.name },
      { label: "Đợt mở bán", value: item?.saleOpeningEvent?.name },
      { label: "Giá trị niêm yết", value: vnd(item?.listedValue), highlight: true },
      { label: "Thu nhập đối chiếu", value: vnd(item?.totalIncome), highlight: true },
      { label: "Khối", value: item?.saleBlock?.name },
      { label: "Vùng", value: item?.saleArea?.name },
      { label: "Chi nhánh", value: item?.branch?.name },
    ],
    // Nhân viên: mở thẳng chi tiết thu nhập đối chiếu của locking.
    onOpen: (item) => `${EMP_BASE_PATH}/tai-chinh/thu-nhap/doi-chieu/${item?.lockingId}`,
  },
  vung: {
    title: (name) => `Thu nhập vùng ${name || ""}`.trim(),
    listApi: getIncomeSummaryAreaLockingsApi,
    idParam: "saleAreaId",
    expandable: true,
    cardTitle: (item) => `${item?.apartment?.code || ""}`,
    rows: (item) => [
      { label: "Dự án", value: item?.project?.name },
      { label: "Đợt mở bán", value: item?.saleOpeningEvent?.name },
      { label: "Giá trị niêm yết", value: vnd(item?.listedValue) },
      { label: "Thu nhập đối chiếu", value: vnd(item?.totalIncome), highlight: true },
    ],
  },
  khoi: {
    title: (name) => `Thu nhập khối ${name || ""}`.trim(),
    listApi: getIncomeSummaryBlockLockingsApi,
    idParam: "saleBlockId",
    expandable: true,
    cardTitle: (item) => `${item?.apartment?.code || ""}`,
    rows: (item) => [
      { label: "Dự án", value: item?.project?.name },
      { label: "Đợt mở bán", value: item?.saleOpeningEvent?.name },
      { label: "Giá trị niêm yết", value: vnd(item?.listedValue) },
      { label: "Thu nhập đối chiếu", value: vnd(item?.totalIncome), highlight: true },
    ],
  },
};

const IncomeLockingList = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { kind, id } = useParams();
  const [search] = useSearchParams();
  const month = search.get("month");
  const year = search.get("year");
  const groupName = search.get("name") || "";
  const cfg = KINDS[kind];

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const reqId = useRef(0);

  const fetchPage = useCallback(
    async (p, mode) => {
      if (!cfg || !id) return;
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const params = { page: p, limit: LIMIT, [cfg.idParam]: id };
        if (month) params.month = Number(month);
        if (year) params.year = Number(year);
        const data = await cfg.listApi(params);
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setTotal(data?.pagination?.total ?? list.length);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [cfg, id, month, year]
  );

  useEffect(() => {
    fetchPage(1, "replace");
  }, [fetchPage]);

  if (!cfg) {
    return (
      <Box maxW="480px" mx="auto">
        <AppBar title="Quản lý thu nhập" />
        <EmptyText text="Không tìm thấy loại thống kê" />
      </Box>
    );
  }

  // Vùng/Khối: mở "Chi tiết thu nhập nhân viên" của căn đó (khớp locking-detail của app).
  const openDetail = (item) => {
    if (cfg.onOpen) {
      navigate(cfg.onOpen(item));
      return;
    }
    const q = new URLSearchParams({
      lockingId: item?.lockingId || "",
      apartmentCode: item?.apartment?.code || "",
      name: groupName,
      month: month || "",
      year: year || "",
    });
    navigate(`${EMP_BASE_PATH}/quan-ly/thu-nhap/${kind}/${id}/can-ho?${q.toString()}`);
  };

  const renderRows = (item) =>
    cfg.rows(item).map((r) => (
      <RowKV key={r.label} label={r.label} value={r.value || "-"} valueColor={r.highlight ? M.primary : undefined} />
    ));

  // ===================== Bản WEB (desktop) =====================
  // Cột bảng dựng từ chính cfg.rows đang dùng cho card mobile.
  if (isDesktop) {
    const columns = items.length ? cfg.rows(items[0]).map((r) => r.label) : [];
    const rows = items.map((it, i) => {
      const cells = {};
      cfg.rows(it).forEach((r) => {
        cells[r.label] = r.value || "-";
      });
      return {
        id: it?.lockingId || i,
        raw: it,
        name: cfg.cardTitle(it) || "-",
        highlights: cfg.rows(it).reduce((acc, r) => {
          acc[r.label] = !!r.highlight;
          return acc;
        }, {}),
        ...cells,
      };
    });

    return (
      <DesktopPage
        title={cfg.title(groupName)}
        right={<DesktopBack onClick={() => navigate(-1)} />}
      >
        <DesktopStatRow items={[{ key: "total", title: "Tổng số giao dịch", total: total || 0 }]} />

        <DesktopPanel p="0">
          <Text fontSize="16px" fontWeight={600} color={M.baseContent} p="16px">
            {month && year
              ? `Danh sách giao dịch tháng ${month} năm ${year}`
              : "Danh sách giao dịch"}
          </Text>

          <DesktopTable
            loading={loading && !items.length}
            onRowClick={(row) => openDetail(row.raw)}
            rows={rows}
            headers={[
              { key: "name", label: "Giao dịch", align: "left" },
              ...columns.map((label) => ({
                key: label,
                label,
                align: "left",
                render: (row) => (
                  <Text
                    fontSize="14px"
                    fontWeight={row.highlights[label] ? 600 : 400}
                    color={row.highlights[label] ? M.primary : undefined}
                  >
                    {row[label]}
                  </Text>
                ),
              })),
            ]}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title={cfg.title(groupName)} />
      <Box px="16px" pt="12px">
        <SummaryCard text="TỔNG SỐ GIAO DỊCH" total={total} />

        <Text fontSize="14px" fontWeight={600} color={M.baseContent} mt="16px" mb="14px">
          {month && year ? `Danh sách giao dịch tháng ${month} năm ${year}` : "Danh sách giao dịch"}
        </Text>

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText />
        ) : (
          <Flex direction="column" gap="12px">
            {items.map((it, i) => {
              const key = it?.lockingId || i;
              if (cfg.expandable) {
                return (
                  <ExpandableCard
                    key={key}
                    open={!!expanded[key]}
                    onToggle={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))}
                    onDetailClick={() => openDetail(it)}
                    header={
                      <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
                        {cfg.cardTitle(it) || "-"}
                      </Text>
                    }
                  >
                    {renderRows(it)}
                  </ExpandableCard>
                );
              }
              return (
                <Box
                  as="button"
                  type="button"
                  key={key}
                  w="100%"
                  textAlign="left"
                  bg="white"
                  border="1px solid"
                  borderColor={M.border}
                  borderRadius="12px"
                  p="14px"
                  onClick={() => openDetail(it)}
                >
                  <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
                    {cfg.cardTitle(it) || "-"}
                  </Text>
                  <Box h="1px" bg={M.border} mt="12px" mb="12px" />
                  <Flex direction="column" gap="8px">
                    {renderRows(it)}
                  </Flex>
                </Box>
              );
            })}
            <LoadMore hasNext={hasNext} loading={loading} onClick={() => fetchPage(page + 1, "append")} />
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default IncomeLockingList;
